import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, UserCircle, ShoppingCart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Topbar() {
  const { user, logout } = useAuth();
  const { count, canBuy } = useCart();
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState("");

  // Per l'azienda mostriamo la ragione sociale al posto del nome del referente.
  useEffect(() => {
    let cancelled = false;
    if (user?.role === "azienda") {
      api.get("/org")
        .then(({ data }) => { if (!cancelled) setOrgName(data?.ragione_sociale || ""); })
        .catch(() => {});
    } else {
      setOrgName("");
    }
    return () => { cancelled = true; };
  }, [user?.role]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const displayName =
    user?.role === "azienda"
      ? orgName || "Azienda"
      : `${user?.first_name || ""} ${user?.last_name || ""}`.trim();

  const roleLabel = {
    super_admin: "Super Admin",
    formatore: "Formatore",
    studente: "Studente",
    azienda: "Azienda",
  }[user?.role] || "";

  const homeForRole = {
    super_admin: "/admin",
    formatore: "/formatore",
    studente: "/studente",
    azienda: "/azienda",
  }[user?.role] || "/";

  return (
    <header
      data-testid="app-topbar"
      className="bg-white border-b border-brand-line h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-40"
    >
      <button
        onClick={() => navigate(homeForRole)}
        className="flex items-center gap-2.5"
        data-testid="topbar-brand"
      >
        <img
          src={`${process.env.PUBLIC_URL}/logo-academy-full.png`}
          alt="Buro Academy"
          className="h-8 w-auto"
        />
      </button>

      <div className="flex items-center gap-3 sm:gap-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="topbar-user-menu"
              className="flex items-center gap-2 rounded-sm hover:bg-brand-blue/5 px-2 py-1.5 transition-colors"
            >
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-sm bg-brand-blue/10 text-brand-blue">
                <UserCircle size={18} />
              </span>
              <div className="hidden sm:flex flex-col items-start leading-tight" data-testid="topbar-user">
                <span className="text-sm font-semibold text-brand-ink">
                  {displayName}
                </span>
                <span className="text-xs text-brand-muted">{roleLabel}</span>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-sm w-52">
            <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="topbar-profile-link"
              onClick={() => navigate("/profilo")}
              className="cursor-pointer"
            >
              <UserCircle size={14} className="mr-2" /> Il mio profilo
            </DropdownMenuItem>
            {canBuy && (
              <DropdownMenuItem
                data-testid="topbar-cart-link"
                onClick={() => navigate("/carrello")}
                className="cursor-pointer"
              >
                <ShoppingCart size={14} className="mr-2" /> Carrello
                {count > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-brand-blue text-white text-[11px] font-semibold">
                    {count}
                  </span>
                )}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="topbar-logout"
              onClick={handleLogout}
              className="cursor-pointer text-red-600 focus:text-red-700"
            >
              <LogOut size={14} className="mr-2" /> Esci
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          data-testid="logout-btn"
          onClick={handleLogout}
          variant="outline"
          className="hidden md:inline-flex rounded-sm border-brand-blue text-brand-blue hover:bg-brand-blue/5 h-9"
        >
          <LogOut size={16} className="mr-1.5" />
          Esci
        </Button>
      </div>
    </header>
  );
}
